import { Account } from '@betty/account/state';
import { FIELDS, INITIAL_STATE } from './AddressVerification.content';
import {
  AddressVerificationField,
  AddressVerificationState,
} from './AddressVerification.types';

const parseAddress = (address: string) => {
  // Regular expression to capture building number, street name, and unit (if any)
  const regex = /^(\d+)\s+([a-zA-Z\s]+)\s*(Apt|Suite|Unit)?\s*(\d*\w*)?/i;

  const match = address.match(regex);

  if (match) {
    const buildingNumber = match[1]; // Captured building number
    const streetName = match[2].trim(); // Captured street name, with leading/trailing spaces removed
    const unitType = match[3] || ''; // Captured unit type (Apt, Suite, etc.) if any
    const unitNumber = match[4] || ''; // Captured unit number or identifier

    return {
      buildingNumber: buildingNumber || null,
      streetName: streetName || null,
      unit: unitType ? `${unitType} ${unitNumber}`.trim() : null, // Combine unit type and number if exists
    };
  }
};

const isCityValid = (city: string) =>
  /^[a-zA-Z',.èéêëîïôœùûüÀÂÄÆÇÈÉÊËÎÏÔŒÙÛÜàâäæçÄÖÜßäöü\s-]{1,40}$/.test(city);

const isPostalCodeValid = (code: string) => /^[0-9a-zA-Z- ]+$/.test(code);

const isAddressValid = (address: string) =>
  /.+/.test(address) && !address.match(/[!@$%^&*()?":{}|<>]/g);

export const buildInitialState = (
  account?: Account,
): AddressVerificationState => {
  if (!account || !account.personalDetails) {
    return INITIAL_STATE;
  }

  const { city, postalCode, addressLine1 } = account.personalDetails;
  const parsedAddress = parseAddress(addressLine1);

  return {
    city: city || '',
    postalCode: postalCode || '',
    building: parsedAddress?.buildingNumber || '',
    street: parsedAddress?.streetName || '',
    unit: parsedAddress?.unit || '',
  };
};

export const createFieldHandlers = (
  onChange: (value: string, field: AddressVerificationField) => void,
  onSubmitEditing: (field: AddressVerificationField) => void,
) =>
  FIELDS.reduce<
    Partial<
      Record<
        AddressVerificationField,
        {
          onChange: (value: string) => void;
          onSubmitEditing: () => void;
        }
      >
    >
  >(
    (accumulator, field) => ({
      ...accumulator,
      [field]: {
        onChange: (value: string) => onChange(value, field),
        onSubmitEditing: () => onSubmitEditing(field),
      },
    }),
    {},
  );

const VALIDATION_MAPPING = {
  [AddressVerificationField.UNIT]: () => true,
  [AddressVerificationField.CITY]: isCityValid,
  [AddressVerificationField.STREET]: isAddressValid,
  [AddressVerificationField.BUILDING]: isAddressValid,
  [AddressVerificationField.POSTAL_CODE]: isPostalCodeValid,
};

export const buildErrors = (state: AddressVerificationState) =>
  FIELDS.reduce<
    Partial<Record<AddressVerificationField, 'invalidField' | undefined>>
  >(
    (accumulator, field) => ({
      ...accumulator,
      [field]: VALIDATION_MAPPING[field](state[field])
        ? undefined
        : 'invalidField',
    }),
    {},
  );

export const formatAddress = (building: string, street: string, unit: string) =>
  `${unit}${unit ? '-' : ''}${building} ${street}`.trim();
